import styled from "@emotion/styled";
import {
	DragContent,
	ExerciseDragAndDropContext,
} from "@ui/pages/program-builder/drag-drop/program-builder-drag-drop-context";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import { motion } from "framer-motion";
import React, { CSSProperties, useContext, useEffect, useState } from "react";
import { DragStart, DragUpdate } from "@hello-pangea/dnd";

export const ITEM_SPACING = 8;
export const CARD_BORDER_SIZE = 1;
export const SESSION_DROPPABLE_HORIZONTAL_PADDING = 8;
export const DayExercisePlaceholder: React.FC<{ dayIndex: number; display: boolean; className?: string }> = ({
	dayIndex,
	display,
	className,
}) => {
	const placeholderStyle = usePlaceholderStyle(dayIndex);

	return display ? (
		<Placeholder
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ ease: "easeInOut" }}
			style={placeholderStyle}
			className={className}
		/>
	) : null;
};

const usePlaceholderStyle = (index: number) => {
	const [placeholderStyle, setPlaceholderStyle] = useState<CSSProperties>({});
	const dragAndDropContext = useContext(ExerciseDragAndDropContext);

	useEffect(() => {
		const handleDragEnd = () => {
			setPlaceholderStyle({});
		};
		const handleDragStart = (v: DragContent<DragStart>) => {
			const { draggableItem, droppableContainer } = getDraggables(v, index);
			if (draggableItem && droppableContainer) {
				const height = draggableItem.getBoundingClientRect().height;

				const top = Array.from(droppableContainer.children)
					.slice(0, v.content.source.index)
					.filter(value => isNotNil(value))
					.reduce((total, curr) => total + curr.clientHeight + ITEM_SPACING + 2 * CARD_BORDER_SIZE, 0);

				setPlaceholderStyle({ height, top });
			}
		};
		const handleDragUpdate = (v: DragContent<DragUpdate>) => {
			const { draggableItem, droppableContainer } = getDraggables(v, index);
			if (v.content.destination && draggableItem && droppableContainer) {
				const sourceIndex = v.content.source.index;
				const destinationIndex = v.content.destination.index;
				const height = draggableItem.getBoundingClientRect().height;
				const childrenArray = Array.from(droppableContainer.children);

				if (v.content.source.droppableId === v.content.destination.droppableId) {
					const [reorderedItem] = childrenArray.splice(sourceIndex, 1);
					childrenArray.splice(destinationIndex, 0, reorderedItem);
				} else {
					const movedItem = childrenArray[sourceIndex];
					childrenArray.splice(destinationIndex, 0, movedItem);
				}

				const top = childrenArray
					.slice(0, destinationIndex)
					.filter(value => isNotNil(value))
					.reduce((total, curr) => total + curr.clientHeight + ITEM_SPACING + 2 * CARD_BORDER_SIZE, 0);

				setPlaceholderStyle({ height, top });
			}
		};
		dragAndDropContext?.onDragEnd.add(handleDragEnd);
		dragAndDropContext?.onDragStart.add(handleDragStart);
		dragAndDropContext?.onDragUpdate.add(handleDragUpdate);
		return () => {
			dragAndDropContext?.onDragEnd.remove(handleDragEnd);
			dragAndDropContext?.onDragStart.remove(handleDragStart);
			dragAndDropContext?.onDragUpdate.remove(handleDragUpdate);
		};
	}, [dragAndDropContext?.onDragEnd, dragAndDropContext?.onDragStart, dragAndDropContext?.onDragUpdate, index]);

	return placeholderStyle;
};

const getDraggables = (v: DragContent<DragStart> | DragContent<DragUpdate> | null, index: number) => {
	if (v) {
		const draggableItem = document.getElementById(v.content.draggableId);
		const droppableContainer = document.getElementById("droppable_" + index);
		return { draggableItem, droppableContainer };
	}
	return { draggableItem: null, droppableContainer: null };
};

const Placeholder = styled(motion.div)`
	position: absolute;
	border-radius: 8px;
	background-color: ${colors.primaryBackground};
	width: calc(100% - ${SESSION_DROPPABLE_HORIZONTAL_PADDING * 2}px);
	z-index: 2;
	left: ${SESSION_DROPPABLE_HORIZONTAL_PADDING}px;
	transition: top 0.1s ease-in;
`;

